import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  getDocs, 
  getDoc,
  orderBy,
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase/firebase';

// Credit Cards Collection
const CREDIT_CARDS_COLLECTION = 'creditCards';
const BUSINESS_ENTITIES_COLLECTION = 'businessEntities';

/**
 * Get all credit cards for a user
 * @param {string} userId - User ID
 * @param {string} type - 'personal' or 'business'
 * @param {string} businessEntityId - Optional business entity ID for business cards
 * @param {Object} scopeOptions - Scope filtering options
 * @param {string} scopeOptions.mentorId - Mentor ID for managed scope filtering
 * @param {boolean} scopeOptions.personalOnly - If true, only return personal scope (no mentorId)
 * @returns {Promise<Array>} Array of credit card documents
 */
export const getUserCreditCards = async (userId, type = null, businessEntityId = null, scopeOptions = {}) => {
  try {
    // Build base query with userId
    let constraints = [where('userId', '==', userId)];

    if (type) {
      constraints.push(where('type', '==', type));
    }

    if (businessEntityId) {
      constraints.push(where('businessEntityId', '==', businessEntityId));
    }

    // IMPORTANT: Add mentorId to query at Firestore level for security rules
    // Firestore evaluates rules per-document, so we must filter at query level
    // to only request documents the caller has permission to read
    if (scopeOptions.mentorId) {
      // Managed scope: query for cards with specific mentorId
      constraints.push(where('mentorId', '==', scopeOptions.mentorId));
    }
    // Note: For personalOnly, we can't use where('mentorId', '==', null) 
    // because Firestore doesn't index missing fields. Instead, personal cards
    // simply don't have mentorId field, and the user owns them (userId == auth.uid)

    let q = query(collection(db, CREDIT_CARDS_COLLECTION), ...constraints);

    // Note: orderBy requires a composite index if combined with where clauses
    // For now, we'll sort in memory to avoid index requirements
    const querySnapshot = await getDocs(q);
    const cards = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();

      // Additional client-side filtering for personalOnly
      // (can't filter at Firestore level for missing fields)
      if (scopeOptions.personalOnly && data.mentorId) {
        // Skip cards with mentorId if personalOnly is true
        return;
      }

      cards.push({
        id: doc.id,
        ...data,
      });
    });

    // Sort by dateCreated in descending order (newest first)
    cards.sort((a, b) => {
      const dateA = a.dateCreated?.toDate ? a.dateCreated.toDate() : new Date(a.dateCreated || 0);
      const dateB = b.dateCreated?.toDate ? b.dateCreated.toDate() : new Date(b.dateCreated || 0);
      return dateB - dateA;
    });

    return cards;
  } catch (error) {
    console.error('Error fetching credit cards:', error);
    throw error;
  }
};

/**
 * Get a single credit card by ID
 * @param {string} cardId - Credit card document ID
 * @returns {Promise<Object>} Credit card document
 */
export const getCreditCard = async (cardId) => {
  try {
    const docRef = doc(db, CREDIT_CARDS_COLLECTION, cardId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching credit card:', error);
    throw error;
  }
};

/**
 * Create a new credit card
 * @param {string} userId - User ID
 * @param {Object} cardData - Credit card data
 * @param {string} mentorId - Optional mentor ID for managed scope
 * @returns {Promise<string>} Document ID of created card
 */
export const createCreditCard = async (userId, cardData, mentorId = null) => {
  try {
    // Convert dateCreated string to Firestore timestamp if it's a string
    let dateCreated = cardData.dateCreated;
    if (typeof dateCreated === 'string' && dateCreated) {
      dateCreated = Timestamp.fromDate(new Date(dateCreated));
    }

    // Convert lastCreditIncreaseDate string to Firestore timestamp if it's a string
    let lastCreditIncreaseDate = cardData.lastCreditIncreaseDate;
    if (lastCreditIncreaseDate && typeof lastCreditIncreaseDate === 'string') {
      lastCreditIncreaseDate = Timestamp.fromDate(new Date(lastCreditIncreaseDate));
    }

    const cardDoc = {
      userId,
      ...cardData,
      dateCreated: dateCreated || serverTimestamp(),
      lastCreditIncreaseDate: lastCreditIncreaseDate || dateCreated || serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Add mentorId if in managed scope
    if (mentorId) {
      cardDoc.mentorId = mentorId;
    }

    const docRef = await addDoc(collection(db, CREDIT_CARDS_COLLECTION), cardDoc);
    return docRef.id;
  } catch (error) {
    console.error('Error creating credit card:', error);
    throw error;
  }
};

/**
 * Update an existing credit card
 * @param {string} cardId - Credit card document ID
 * @param {Object} cardData - Updated credit card data
 * @returns {Promise<void>}
 */
export const updateCreditCard = async (cardId, cardData) => {
  try {
    // Convert dateCreated string to Firestore timestamp if it's a string
    const updateData = { ...cardData };
    if (updateData.dateCreated && typeof updateData.dateCreated === 'string') {
      updateData.dateCreated = Timestamp.fromDate(new Date(updateData.dateCreated));
    }
    
    // Convert lastCreditIncreaseDate string to Firestore timestamp if it's a string
    if (updateData.lastCreditIncreaseDate && typeof updateData.lastCreditIncreaseDate === 'string') {
      updateData.lastCreditIncreaseDate = Timestamp.fromDate(new Date(updateData.lastCreditIncreaseDate));
    }

    const cardRef = doc(db, CREDIT_CARDS_COLLECTION, cardId);
    await updateDoc(cardRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating credit card:', error);
    throw error;
  }
};

/**
 * Delete a credit card
 * @param {string} cardId - Credit card document ID
 * @returns {Promise<void>}
 */
export const deleteCreditCard = async (cardId) => {
  try {
    const cardRef = doc(db, CREDIT_CARDS_COLLECTION, cardId);
    await deleteDoc(cardRef);
  } catch (error) {
    console.error('Error deleting credit card:', error);
    throw error;
  }
};

// Business Entities Functions

/**
 * Get all business entities for a user
 * @param {string} userId - User ID
 * @param {Object} scopeOptions - Scope filtering options
 * @param {string} scopeOptions.mentorId - Mentor ID for managed scope filtering
 * @param {boolean} scopeOptions.personalOnly - If true, only return personal scope (no mentorId)
 * @returns {Promise<Array>} Array of business entity documents
 */
export const getUserBusinessEntities = async (userId, scopeOptions = {}) => {
  try {
    // Build base query with userId
    let constraints = [where('userId', '==', userId)];

    // IMPORTANT: Add mentorId to query at Firestore level for security rules
    // Firestore evaluates rules per-document, so we must filter at query level
    // to only request documents the caller has permission to read
    if (scopeOptions.mentorId) {
      // Managed scope: query for entities with specific mentorId
      constraints.push(where('mentorId', '==', scopeOptions.mentorId));
    }

    const q = query(collection(db, BUSINESS_ENTITIES_COLLECTION), ...constraints);

    const querySnapshot = await getDocs(q);
    const entities = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();

      // Additional client-side filtering for personalOnly
      // (can't filter at Firestore level for missing fields)
      if (scopeOptions.personalOnly && data.mentorId) {
        // Skip entities with mentorId if personalOnly is true
        return;
      }

      entities.push({
        id: doc.id,
        ...data,
      });
    });

    // Sort by dateCreated in descending order (newest first)
    entities.sort((a, b) => {
      const dateA = a.dateCreated?.toDate ? a.dateCreated.toDate() : new Date(a.dateCreated || 0);
      const dateB = b.dateCreated?.toDate ? b.dateCreated.toDate() : new Date(b.dateCreated || 0);
      return dateB - dateA;
    });

    return entities;
  } catch (error) {
    console.error('Error fetching business entities:', error);
    throw error;
  }
};

/**
 * Get a single business entity by ID
 * @param {string} entityId - Business entity document ID
 * @returns {Promise<Object>} Business entity document
 */
export const getBusinessEntity = async (entityId) => {
  try {
    const docRef = doc(db, BUSINESS_ENTITIES_COLLECTION, entityId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
      };
    }
    return null;
  } catch (error) {
    console.error('Error fetching business entity:', error);
    throw error;
  }
};

/**
 * Create a new business entity
 * @param {string} userId - User ID
 * @param {Object} entityData - Business entity data
 * @param {string} mentorId - Optional mentor ID for managed scope
 * @returns {Promise<string>} Document ID of created entity
 */
export const createBusinessEntity = async (userId, entityData, mentorId = null) => {
  try {
    // Convert dateCreated string to Firestore timestamp if it's a string
    let dateCreated = entityData.dateCreated;
    if (typeof dateCreated === 'string' && dateCreated) {
      dateCreated = Timestamp.fromDate(new Date(dateCreated));
    }

    const entityDoc = {
      userId,
      ...entityData,
      dateCreated: dateCreated || serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Add mentorId if in managed scope
    if (mentorId) {
      entityDoc.mentorId = mentorId;
    }

    const docRef = await addDoc(collection(db, BUSINESS_ENTITIES_COLLECTION), entityDoc);
    return docRef.id;
  } catch (error) {
    console.error('Error creating business entity:', error);
    throw error;
  }
};

/**
 * Update an existing business entity
 * @param {string} entityId - Business entity document ID
 * @param {Object} entityData - Updated business entity data
 * @returns {Promise<void>}
 */
export const updateBusinessEntity = async (entityId, entityData) => {
  try {
    // Convert dateCreated string to Firestore timestamp if it's a string
    const updateData = { ...entityData };
    if (updateData.dateCreated && typeof updateData.dateCreated === 'string') {
      updateData.dateCreated = Timestamp.fromDate(new Date(updateData.dateCreated));
    }

    const entityRef = doc(db, BUSINESS_ENTITIES_COLLECTION, entityId);
    await updateDoc(entityRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating business entity:', error);
    throw error;
  }
};

/**
 * Delete a business entity
 * @param {string} entityId - Business entity document ID
 * @returns {Promise<void>}
 */
export const deleteBusinessEntity = async (entityId) => {
  try {
    const entityRef = doc(db, BUSINESS_ENTITIES_COLLECTION, entityId);
    await deleteDoc(entityRef);
  } catch (error) {
    console.error('Error deleting business entity:', error);
    throw error;
  }
};

